import * as React from "react";
import { pick } from "lodash";
import {
  BktDataTable,
  Filter,
  eFilter,
  IBktDataTableData,
  SortType
} from "front-end-lib/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleXmark,
  faPenToSquare
} from "@fortawesome/free-regular-svg-icons";
import { defaultDataTableConfig } from "../../../config";
import {
  classConstants,
  activeStatuses,
  yesNoFilters
} from "../../../constants";
import { IStudyQualification, IFilter } from "../../../model";
import { isLoading, sortStatus, filterDataForDataTable } from "../../../utils";
import { MddStatus } from "../../common";
import { eStudySaga } from "../../../sagas";

interface IMddShowStudyQualificationsTableProps {
  studyQuals: IStudyQualification[] | undefined;
  noRecordsMessage: string | undefined;
  initialStudyQuals: React.MutableRefObject<IStudyQualification[]>;
  setStudyQuals: (quals: IStudyQualification[]) => void;
  onEdit: (qual: IStudyQualification) => void;
  onDelete: (qual: IStudyQualification) => void;
}

export const MddShowStudyQualificationsTable = ({
  studyQuals,
  noRecordsMessage,
  initialStudyQuals,
  setStudyQuals,
  onEdit,
  onDelete
}: IMddShowStudyQualificationsTableProps) => {
  const filterVals = React.useRef(new Map<string, IFilter>());

  const handleFilterChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
    exactMatch = false
  ) => {
    const value = e.target.value ? e.target.value.trim() : "";
    const key = e.target.name;
    if (initialStudyQuals.current && initialStudyQuals.current.length) {
      const filteredStudyQuals = filterDataForDataTable(
        { key, value: { value, exactMatch } },
        filterVals.current,
        initialStudyQuals.current
      );
      setStudyQuals(filteredStudyQuals || []);
    } else if (initialStudyQuals.current) {
      setStudyQuals(initialStudyQuals.current || []);
    }
  };

  const getActions = React.useCallback(
    (qual: IStudyQualification) => {
      return (
        <div className="tbl--body-row-actions">
          <span onClick={() => onEdit(qual)}>
            <FontAwesomeIcon
              icon={faPenToSquare}
              fixedWidth
              fontSize={"1.1rem"}
              id="editIcon"
              data-testid="editIcon"
              title="Edit"
            />
          </span>
          <span
            onClick={() => onDelete(qual)}
            className={qual.CanDelete ? "" : "notVisible"}
            id={`spanDeleteIcon${qual.CanDelete ? "" : "_hidden"}`}
            data-testid={`spanDeleteIcon${qual.CanDelete ? "" : "_hidden"}`}
          >
            <FontAwesomeIcon
              icon={faCircleXmark}
              fixedWidth
              fontSize={"1.1rem"}
              id="deleteCircleIcon"
              data-testid="svgDeleteIcon"
              title="Delete"
            />
          </span>
        </div>
      );
    },
    [onEdit, onDelete]
  );

  const tableData = React.useMemo(() => {
    return (
      studyQuals
        ? studyQuals.map((sq: IStudyQualification) =>
            pick(
              Object.assign(sq, { key: sq.StudyQualificationId, qual: sq }),
              "key",
              "QualificationName",
              "ScaleName",
              "IsActive",
              "IsPrimary",
              "qual"
            )
          )
        : []
    ) as IBktDataTableData[];
  }, [studyQuals]);

  return (
    <BktDataTable
      {...defaultDataTableConfig}
      data={tableData}
      defaultSortProp="ScaleName"
      loading={isLoading(`undefined_${eStudySaga.FETCH_STUDY_QUALIFICATIONS}`)}
      noRecordsMessage={noRecordsMessage}
      columnProps={[
        {
          label: "Qualification",
          sortProp: { sortPropName: "QualificationName", desc: true },
          filter: new Filter(eFilter.Text, {
            debounceInterval: 0,
            textProps: {
              className: classConstants.TEXT_FILTER_CLASS,
              id: "qualFilter",
              name: "QualificationName",
              onChange: handleFilterChange
            }
          }),
          tableCellProps: { id: "qualHeader" },
          divProps: { id: "qualHeader_div" }
        },
        {
          label: "Scale",
          sortProp: { sortPropName: "ScaleName", desc: true },
          filter: new Filter(eFilter.Text, {
            debounceInterval: 0,
            textProps: {
              className: classConstants.TEXT_FILTER_CLASS,
              id: "scaleFilter",
              name: "ScaleName",
              onChange: handleFilterChange
            }
          }),
          tableCellProps: { id: "scaleHeader" },
          divProps: { id: "scaleHeader_div" }
        },
        {
          label: "Status",
          sortProp: {
            sortPropName: "IsActive",
            desc: false,
            sortType: "Custom" as SortType,
            alg: sortStatus
          },
          filter: new Filter(eFilter.Select, {
            textProps: {
              id: "qualStatusFilterText",
              name: "isActive",
              className: "tbl--filter-select",
              onChange: (e) => handleFilterChange(e, true),
              InputLabelProps: { shrink: filterVals.current.has("isActive") }
            },
            selectMenuProps: { MenuProps: { className: "bracket-Select" } },
            menuProps: {},
            selectData: activeStatuses,
            defaultSelect: true
          }),
          tableCellProps: { id: "qualificationStatusHeader" },
          divProps: { id: "qualificationStatus_div" },
          formatProp: {
            formatPropName: "IsActive",
            format: (val: boolean) => <MddStatus isActive={val} />
          }
        },
        {
          label: "Primary Qualification",
          sortProp: {
            sortPropName: "IsPrimary",
            desc: false
          },
          filter: new Filter(eFilter.Select, {
            textProps: {
              id: "primaryQualFilterText",
              name: "IsPrimary",
              className: "tbl--filter-select",
              onChange: (e) => handleFilterChange(e, true),
              InputLabelProps: { shrink: filterVals.current.has("IsPrimary") }
            },
            selectMenuProps: { MenuProps: { className: "bracket-Select" } },
            menuProps: {},
            selectData: yesNoFilters,
            defaultSelect: true
          }),
          tableCellProps: { id: "primaryQualificationHeader" },
          divProps: { id: "primaryQualification_div" },
          formatProp: {
            formatPropName: "IsPrimary",
            format: (val: boolean) => (val ? "Yes" : "No")
          }
        },
        {
          label: "Actions",
          tableCellProps: { id: "actions" },
          divProps: { id: "actions_div" },
          formatProp: {
            formatPropName: "qual",
            format: getActions
          }
        }
      ]}
    />
  );
};
